import React, { useEffect, useState } from 'react';
import {
    FaMoneyBillWave,
    FaCreditCard,
    FaChartLine,
    FaPercentage,
    FaFileInvoiceDollar,
    FaCalendarAlt,
    FaCheckCircle,
    FaExclamationCircle
} from 'react-icons/fa';
import loanService from '../../services/api/loan.service';
import { BiLoaderAlt } from 'react-icons/bi';
import { Link } from 'react-router-dom';

export interface LoanDetail {
    total_credit_amount: number;
    available_credit_amount: number;
    principal_outstanding_amount: number;
    current_applicable_interest_rate: number;
    pledged_portfolio_amount: number;
    over_utilization_amount: number;
    charges_due_amount: number;
    credit_status: string;
    processing_charges: number;
    interest_due_amount: number;
    interest_due_date: string;
    renewal_date: string;
    pledged_portfolio_items: {
        asset_repository: string;
        amc_name: string | null;
        amc_code: string | null;
        folio_no: string;
        scheme_code: string;
        scheme_name: string;
        isin_no: string;
        scheme_type: string | null;
        total_available_units: number;
    }[];
}

const LoanInfo: React.FC = () => {
    const [details, setDetails] = useState<LoanDetail>({} as LoanDetail);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const fetchLoanDetails = async () => {
        try {
            const response = await loanService.getLoanDetails();
            setDetails(response.data);
            setNotFound(false);
        } catch (error) {
            setNotFound(true);
            console.log('Error fetching loan details:', error);

        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLoanDetails();
    }, []);

    if (notFound) {
        return (
            <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center">
                <div className="flex flex-col items-center">
                    <p className="mt-2 text-lg text-gray-600 mb-5">There is no active loan</p>
                    {/* <Link to="/loan/apply" className='bg-primary-900 text-white py-2 px-3 rounded-md'>Apply Loan</Link> */}
                </div>
            </div>
        )
    }

    if (loading) {
        return (
            <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center">
                <div className="flex flex-col items-center">
                    <BiLoaderAlt className="text-primary-800 text-3xl animate-spin" />
                    <p className="mt-2 text-sm text-gray-600">Loading loan details...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="container mx-auto p-4 min-h-screen">
            <div className="max-w-6xl mx-auto border rounded-xl overflow-hidden">
                {/* Loan Summary Header */}
                <div className="bg-gradient-to-r from-primary-800 to-primary-900 text-white p-6">
                    <div className="flex justify-between items-center">
                        <h1 className="text-2xl font-bold tracking-wide">Loan Details</h1>
                        <div className="flex items-center">
                            <span className={`flex items-center px-4 py-2 rounded-full text-sm font-semibold 
                                ${details.credit_status === 'ACTIVE'
                                    ? 'bg-green-500 text-white'
                                    : 'bg-red-500 text-white'}`}>
                                {details.credit_status === 'ACTIVE'
                                    ? <FaCheckCircle className="mr-2" />
                                    : <FaExclamationCircle className="mr-2" />}
                                {details.credit_status}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Loan Overview Grid */}
                <div className="grid md:grid-cols-2 gap-6 p-6">
                    <div className="space-y-4">
                        <Link to="/loan/apply" className='bg-primary-900 text-white py-2 px-3 rounded-md mb-4'>Withdraw/Repay?</Link>

                        <DetailCard
                            icon={<FaMoneyBillWave className="text-blue-500" />}
                            label="Total Credit"
                            value={`₹${details?.total_credit_amount?.toLocaleString()}`}
                        />
                        <DetailCard
                            icon={<FaCreditCard className="text-primary-500" />}
                            label="Available Credit"
                            value={`₹${details?.available_credit_amount?.toLocaleString()}`}
                        />
                        {details?.principal_outstanding_amount && <DetailCard
                            icon={<FaChartLine className="text-green-500" />}
                            label="Principal Outstanding"
                            value={`₹${details?.principal_outstanding_amount?.toLocaleString()}`}
                        />}
                    </div>
                    <div className="space-y-4">
                        <DetailCard
                            icon={<FaPercentage className="text-red-500" />}
                            label="Current Interest Rate"
                            value={`${details.current_applicable_interest_rate}%`}
                        />
                        <DetailCard
                            icon={<FaFileInvoiceDollar className="text-orange-500" />}
                            label="Pledged Portfolio"
                            value={`₹${details?.pledged_portfolio_amount?.toLocaleString()}`}
                        />
                        <DetailCard
                            icon={<FaCalendarAlt className="text-teal-500" />}
                            label="Renewal Date"
                            value={new Date(details?.renewal_date)?.toLocaleDateString()}
                        />
                    </div>
                </div>

                {/* Portfolio Holdings */}
                <div className="bg-gray-100 p-6">
                    <h2 className="text-xl font-semibold mb-4 text-gray-800">Portfolio Holdings</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full bg-white shadow rounded-lg overflow-hidden">
                            <thead className="bg-blue-50">
                                <tr>
                                    {['Scheme Name', 'Folio No', 'Units', 'ISIN'].map((header) => (
                                        <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {details?.pledged_portfolio_items?.map((item, index) => (
                                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {item?.scheme_name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item?.folio_no}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item?.total_available_units?.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item?.isin_no}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Additional Details */}
                <div className="grid md:grid-cols-2 gap-6 p-6 bg-white">
                    <div className="space-y-4">
                        <DetailCard
                            icon={<FaFileInvoiceDollar className="text-primary-900" />}
                            label="Processing Charges"
                            value={`₹${details?.processing_charges?.toLocaleString()}`}
                        />
                        <DetailCard
                            icon={<FaMoneyBillWave className="text-primary-900" />}
                            label="Interest Due"
                            value={`₹${details?.interest_due_amount?.toLocaleString()}`}
                        />
                    </div>
                    <div className="space-y-4">
                        <DetailCard
                            icon={<FaCalendarAlt className="text-cyan-500" />}
                            label="Interest Due Date"
                            value={new Date(details?.interest_due_date)?.toLocaleDateString()}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

// Detailed Card Component
const DetailCard: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: string | number
}> = ({ icon, label, value }) => (
    <div className="bg-white shadow-md rounded-lg p-4 flex items-center space-x-4 border border-gray-100 hover:shadow-lg transition-all duration-300">
        <div className="p-3 bg-gray-100 rounded-full">
            {icon}
        </div>
        <div className="flex-1">
            <p className="text-sm text-gray-500 mb-1">{label}</p>
            <p className="text-lg font-bold text-gray-800">{value}</p>
        </div>
    </div>
);

export default LoanInfo;